// Pure currency utility functions

export type RatesMap = Record<string, number>;

/**
 * Formats a numeric amount as a localized currency string.
 * Pure: relies only on input parameters.
 */
export function formatCurrency(amount: number, currency?: string, locale?: string): string {
  try {
    // Guard against NaN/Infinity for consistent output
    const safe = typeof amount === 'number' && isFinite(amount) ? amount : 0;
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: currency || 'USD',
      currencyDisplay: 'symbol',
      maximumFractionDigits: 2,
    }).format(safe);
  } catch {
    const safe = typeof amount === 'number' && isFinite(amount) ? amount : 0;
    return `${currency || ''} ${safe.toFixed(2)}`.trim();
  }
}

/**
 * Converts an amount from one currency to another using a map of rates.
 * The rates map should contain numeric rates for ISO codes. If either the
 * source or target rate is missing, the function gracefully falls back
 * mirroring the prior behavior used in the app.
 *
 * Behavior:
 * - If amount is invalid => returns 0
 * - If target ISO not provided or equals from ISO => returns original amount
 * - If target rate missing => returns original amount
 * - If from rate missing or zero => returns amount * targetRate
 * - Else => returns amount * (targetRate / fromRate)
 */
export function convertCurrencyAmount(
  amount: number,
  rates: RatesMap | undefined,
  fromIso: string,
  toIso?: string
): number {
  if (typeof amount !== 'number' || !isFinite(amount)) return 0;
  if (!rates) return amount;
  if (!toIso || toIso === fromIso) return amount;

  const rateFrom = rates[fromIso];
  const rateTo = rates[toIso];

  if (rateTo === undefined) return amount;
  if (!rateFrom || rateFrom === 0) return amount * rateTo;

  return amount * (rateTo / rateFrom);
}
